import * as React from 'react';

import { cn } from '../../lib/utils';

import { useFieldControl } from './form-field';
import type { FieldControlState } from './form-field';

interface InputProps extends React.ComponentProps<'input'> {
    /** Default filled gray matches most form fields; `outlined` is for primary hero fields on white (e.g. agreement name). */
    variant?: 'filled' | 'outlined';
    unit?: string;
}

const inputBaseClasses =
    'flex h-9 w-full rounded-md px-3 py-1 text-base font-normal shadow-none transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 aria-[invalid=true]:border-destructive disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-600 md:text-sm';

const inputVariantClasses: Record<NonNullable<InputProps['variant']>, string> = {
    filled: 'border border-admin-field-border admin-field-fill text-neutral-900 file:text-neutral-900 focus-visible:border-transparent dark:text-neutral-100 dark:placeholder:text-neutral-400 dark:file:text-neutral-100',
    outlined:
        'border border-admin-field-border bg-white text-neutral-900 file:text-neutral-900 focus-visible:border-neutral-400 dark:border-neutral-600 dark:bg-neutral-950 dark:text-neutral-50 dark:placeholder:text-neutral-400 dark:file:text-neutral-50',
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            type,
            variant = 'filled',
            onWheel,
            unit,
            'aria-describedby': ariaDescribedBy,
            'aria-invalid': ariaInvalid,
            'aria-required': ariaRequired,
            ...props
        },
        ref,
    ) => {
        const field: FieldControlState | null = useFieldControl();
        const input = (
            <input
                type={type}
                className={cn(
                    inputBaseClasses,
                    inputVariantClasses[variant],
                    unit && '!pr-16 text-right tabular-nums',
                    className,
                )}
                ref={ref}
                onWheel={(event) => {
                    onWheel?.(event);
                    if (type === 'number' && !event.defaultPrevented) {
                        event.currentTarget.blur();
                    }
                }}
                aria-describedby={ariaDescribedBy ?? field?.describedBy}
                aria-invalid={ariaInvalid ?? (field?.invalid ? true : undefined)}
                aria-required={
                    ariaRequired ??
                    (field?.required && !props.disabled && !props.readOnly ? true : undefined)
                }
                {...props}
            />
        );

        if (!unit) return input;

        return (
            <div className="relative w-full">
                {input}
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-neutral-500"
                >
                    {unit}
                </span>
            </div>
        );
    },
);
Input.displayName = 'Input';

export { Input };
